import { app, translate } from "../app";
import { translations } from "../translations";
import supercluster, { ClusterFeature, PointFeature } from "supercluster";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GeocoderControl from "./geocoder-control";
import { languages } from "../locale";
import {
  AccountBalance,
  Azm,
  Badge,
  Book,
  EmergencyHome,
  EmojiTransportation,
  EscalatorWorning,
  FoodBank,
  Gavel,
  Groups,
  Groups2,
  Home,
  LauncherAssistantOn,
  LiveHelp,
  MedicalServices,
  Payments,
  PinDrop,
  Psychology,
  RightOutlined,
  School,
  VolunteerActivism,
  Woman,
  WorkOutline,
} from "./icons";
import { Button } from "./button";
import { MapCompanyCards } from "./map-company-cards";
import { trackServiceClick, trackServiceDetailsClick } from "../map-analytics";

const MapGL = React.lazy(() =>
  import("react-map-gl").then((module) => ({ default: module.Map })),
);
const NavigationControl = React.lazy(() =>
  import("react-map-gl").then((module) => ({
    default: module.NavigationControl,
  })),
);
const Marker = React.lazy(() =>
  import("react-map-gl").then((module) => ({ default: module.Marker })),
);
const Popup = React.lazy(() =>
  import("react-map-gl").then((module) => ({ default: module.Popup })),
);

const iconMapping = {
  home: Home,
  volunteer_activism: VolunteerActivism,
  escalator_warning: EscalatorWorning,
  badge: Badge,
  school: School,
  payments: Payments,
  food_bank: FoodBank,
  medical_services: MedicalServices,
  gavel: Gavel,
  psychology: Psychology,
  groups: Groups,
  emoji_transportation: EmojiTransportation,
  woman: Woman,
  work_outline: WorkOutline,
  live_help: LiveHelp,
  book: Book,
  account_balance: AccountBalance,
  emergency_home: EmergencyHome,
  groups_2: Groups2,
  launcher_assistant_on: LauncherAssistantOn,
  azm: Azm,
};

const stripHtmlTags = (html: string): string => {
  const regex =
    /<[^>]*>|&[^ ]+ |<img\s+.*?>|<span\s+style="[^"]*">.*?<\/span>|&[A-Za-z]+ /g;
  return html.replace(regex, "");
};

const SWITCHBOARD_MAX_BOUNDS: [[number, number], [number, number]] = [
  [-179, 18],
  [-52, 72],
];

const SWITCHBOARD_DEFAULT_CENTER = { longitude: -98.5795, latitude: 39.8283, zoom: 3 };

// Neutral fallback for missing / unsafe category colors — a deep slate-blue
// that reads on light basemaps and is safe across colorblind types.
const SAFE_PIN_FALLBACK = "#2b6cb0";

/**
 * Keep marker colors legible on a light map and distinguishable for colorblind
 * users. Returns the original color when it's safe, otherwise a corrected tone:
 *  - too-light colors (poor contrast on white) are darkened
 *  - yellow / lime-green hues (hard on light bg AND for red-green colorblind
 *    users) are replaced with the safe fallback
 */
function safePinColor(input?: string): string {
  if (!input) return SAFE_PIN_FALLBACK;
  const hex = input.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex.split("").map((c) => c + c).join("")
      : hex;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return SAFE_PIN_FALLBACK;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);

  // Relative luminance (sRGB, perceptual). Above ~0.62 is too pale on white.
  const lum =
    0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);

  // Hue in degrees, to catch the yellow/lime band (~55°–95°).
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let hue = 0;
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue = (hue * 60 + 360) % 360;
  }
  const sat = max === 0 ? 0 : d / max;

  const isYellowLime = hue >= 55 && hue <= 95 && sat > 0.25;
  if (isYellowLime) return SAFE_PIN_FALLBACK;

  if (lum > 0.62) {
    // Too light: darken toward a readable version of the same hue.
    const factor = 0.6;
    const dr = Math.round(r * factor),
      dg = Math.round(g * factor),
      db = Math.round(b * factor);
    return `#${[dr, dg, db]
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")}`;
  }

  return `#${full}`;
}

function isDefaultSwitchboardCategoryColor(color?: string): boolean {
  if (!color) return false;
  return color.trim().replace(/^#/, "").toLowerCase() === "ffc069";
}

function resolvePinAccent(
  categoryColor: string | undefined,
  isSwitchboard: boolean,
): string {
  if (isSwitchboard && isDefaultSwitchboardCategoryColor(categoryColor)) {
    return "var(--marker-fill)";
  }
  return safePinColor(categoryColor);
}

interface mapProps {
  services: Service[];
  showPopup?: boolean;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  onAISearch?: (query: string) => void;
  showCompanyCards?: boolean;
  recommendedServices?: Service[] | null;
  serviceToNavigate?: Service | null;
  onNavigateComplete?: () => void;
  showIA?: boolean;
  userLocation?: { latitude: number; longitude: number } | null;
  onUserLocationChange?: (
    location: { latitude: number; longitude: number } | null,
  ) => void;
}

const MAP_STYLES: { [key: string]: any | string } = {
  mapbox: "mapbox://styles/signpostglobal/cmheuwfw000e601qw4j6mcm20",
  osm: {
    version: 8,
    sources: {
      "raster-tiles": {
        type: "raster",
        tiles: [
          "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
          "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
          "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
        ],
        tileSize: 256,
      },
    },
    layers: [
      {
        id: "osm-tiles",
        type: "raster",
        source: "raster-tiles",
        minzoom: 3,
        maxzoom: 20,
      },
    ],
  },
  satellite: "mapbox://styles/mapbox/satellite-streets-v12?optimize=true",
  dark: "mapbox://styles/mapbox/dark-v11?optimize=true",
};

function MapStyleControlComponent({ setMapStyle, currentMapStyle }) {
  const [open, setOpen] = useState(false);

  const handleMapStyleChange = (style) => {
    setMapStyle(style);
  };

  let style = "";

  if (typeof currentMapStyle === "string") {
    style = currentMapStyle.includes("dark")
      ? "Dark"
      : currentMapStyle.includes("satellite")
        ? "Satellite"
        : "Mapbox";
  } else {
    style = "OSM";
  }

  return (
    <div className="absolute bottom-8 left-4">
      <div className="flex align-bottom items-end">
        <Button
          ghost
          className="bg-white text-black"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {style}
        </Button>
        {open && (
          <div className="flex bg-white p-2 gap-2 rounded-md ml-1 animate__fadeIn  animate__animated">
            <Button
              ghost
              className="border-gray-300 text-black bg-white"
              onClick={() => handleMapStyleChange(MAP_STYLES.mapbox)}
            >
              Mapbox
            </Button>
            <Button
              ghost
              className="border-gray-300 text-black bg-white"
              onClick={() => handleMapStyleChange(MAP_STYLES.osm)}
            >
              OSM
            </Button>
            <Button
              ghost
              className="border-gray-300 text-black bg-white"
              onClick={() => handleMapStyleChange(MAP_STYLES.satellite)}
            >
              Satellite
            </Button>
            <Button
              ghost
              className="border-gray-300 text-black bg-white"
              onClick={() => handleMapStyleChange(MAP_STYLES.dark)}
            >
              Dark
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function moveMapByPixels(map, longitude, latitude, pixelOffset) {
  const latLngToPixel = map.project([longitude, latitude]);
  const newPixelPosition = {
    x: latLngToPixel.x,
    y: latLngToPixel.y + pixelOffset,
  };
  const newLatLng = map.unproject(newPixelPosition);
  return newLatLng.lat;
}

const pixelOffsetToLatLng = (
  map,
  longitude: number,
  latitude: number,
  offsetX: number,
  offsetY: number,
) => {
  const pixel = map.project([longitude, latitude]);
  const unprojected = map.unproject({
    x: pixel.x + offsetX,
    y: pixel.y + offsetY,
  });
  return { lng: unprojected.lng, lat: unprojected.lat };
};

const COLOCATED_COORD_PRECISION = 5;

const coordKey = (lng: number, lat: number) =>
  `${lng.toFixed(COLOCATED_COORD_PRECISION)},${lat.toFixed(COLOCATED_COORD_PRECISION)}`;

const spiderPixelOffset = (index: number, total: number) => {
  const radius = Math.min(120, 36 + total * 5);
  const angle = (2 * Math.PI * index) / total - Math.PI / 2;
  return {
    x: radius * Math.cos(angle),
    y: radius * Math.sin(angle),
  };
};

type ExpandedMarkerGroup = {
  id: string;
  lng: number;
  lat: number;
  points: Array<{ serviceId: number; category?: number }>;
};

type ClusterOrPoint =
  | ClusterFeature<supercluster.AnyProps>
  | PointFeature<supercluster.AnyProps>;

type MapMarkerItem =
  | { kind: "cluster"; data: ClusterOrPoint }
  | { kind: "point"; data: ClusterOrPoint }
  | {
      kind: "colocated";
      key: string;
      lng: number;
      lat: number;
      points: ClusterOrPoint[];
    };

const areColocatedLeaves = (leaves: ClusterOrPoint[]) => {
  if (leaves.length <= 1) return false;
  const keys = new Set(
    leaves.map((leaf) =>
      coordKey(leaf.geometry.coordinates[0], leaf.geometry.coordinates[1]),
    ),
  );
  return keys.size === 1;
};

const buildMapMarkers = (clusters: ClusterOrPoint[]): MapMarkerItem[] => {
  const result: MapMarkerItem[] = [];
  const individuals: ClusterOrPoint[] = [];

  for (const item of clusters) {
    if (item.properties.cluster) {
      result.push({ kind: "cluster", data: item });
    } else {
      individuals.push(item);
    }
  }

  const groups = new globalThis.Map<string, ClusterOrPoint[]>();
  for (const point of individuals) {
    const [lng, lat] = point.geometry.coordinates;
    const key = coordKey(lng, lat);
    const group = groups.get(key);
    if (group) group.push(point);
    else groups.set(key, [point]);
  }

  for (const [key, group] of groups) {
    const [lng, lat] = group[0].geometry.coordinates;
    if (group.length === 1) {
      result.push({ kind: "point", data: group[0] });
    } else {
      result.push({ kind: "colocated", key, lng, lat, points: group });
    }
  }

  return result;
};

export function Maps({
  services,
  showPopup = true,
  latitude,
  longitude,
  zoom,
  onAISearch,
  showCompanyCards = true,
  recommendedServices,
  serviceToNavigate,
  onNavigateComplete,
  showIA = false,
  userLocation: userLocationProp,
  onUserLocationChange,
}: mapProps) {
  const mapRef = useRef<any>(null);
  const categories = Object.values(app.data.categories.categories) || [];
  // O(1) category lookup by id — replaces a linear categories.filter() run per marker per render.
  const categoryById = useMemo(() => {
    const m = new globalThis.Map<number, (typeof categories)[number]>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [app.data.categories.categories]);
  // O(1) service lookup by id — replaces services.find() calls inside the marker render loops.
  const serviceById = useMemo(() => {
    const m = new globalThis.Map<number, Service>();
    for (const s of services || []) m.set(s.id, s);
    return m;
  }, [services]);
  const [popupInfo, setPopupInfo] = useState<Service | null>(null);
  const [popupCoords, setPopupCoords] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const [clusters, setClusters] = useState<ClusterOrPoint[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapStyle, setMapStyle] = useState<any | string>(MAP_STYLES.mapbox);
  const [selectedCardServiceId, setSelectedCardServiceId] = useState<
    number | undefined
  >(undefined);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [dismissedCardIds, setDismissedCardIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [expandedGroup, setExpandedGroup] = useState<ExpandedMarkerGroup | null>(
    null,
  );
  const [internalUserLocation, setInternalUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const usesExternalUserLocation = userLocationProp !== undefined;
  const userLocation = usesExternalUserLocation
    ? userLocationProp
    : internalUserLocation;
  const isLocatingRef = useRef(false);
  const hasAutoRequestedLocationRef = useRef(false);
  const preserveExpandedOnMoveRef = useRef(false);
  const geocoderRef = useRef<any>(null);
  const locale = (languages[app.locale]?.zendesk as string) ?? app.locale;
  const isSwitchboard = app.country === 42;
  const isOrPrograms = app.country === 43;

  const displayServiceName = (name?: LocalizableText) => {
    if (!name) return "";
    if (isSwitchboard || isOrPrograms) return name["en-US"] ?? "";
    return translate(name);
  };

  const carouselServices = recommendedServices || [];
  const displayedCarouselServices = carouselServices.filter(
    (s) => !dismissedCardIds.has(s.id),
  );

  useEffect(() => {
    setDismissedCardIds(new Set());
  }, [recommendedServices]);

  const applyUserPosition = useCallback((position: GeolocationPosition) => {
    setInternalUserLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });
    isLocatingRef.current = false;
  }, []);

  const applyIpLocation = useCallback(async () => {
    try {
      const response = await fetch("https://ipwho.is/");
      const data = await response.json();
      if (
        data &&
        data.success !== false &&
        typeof data.latitude === "number" &&
        typeof data.longitude === "number"
      ) {
        setInternalUserLocation({
          latitude: data.latitude,
          longitude: data.longitude,
        });
      }
    } catch {
    } finally {
      isLocatingRef.current = false;
    }
  }, []);

  const requestUserLocation = useCallback(() => {
    if (isLocatingRef.current) return;

    isLocatingRef.current = true;

    if (!navigator.geolocation || !window.isSecureContext) {
      applyIpLocation();
      return;
    }

    const options: PositionOptions = {
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 600000,
    };

    navigator.geolocation.getCurrentPosition(
      applyUserPosition,
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          isLocatingRef.current = false;
          return;
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          window.setTimeout(() => {
            navigator.geolocation.getCurrentPosition(
              applyUserPosition,
              applyIpLocation,
              { ...options, maximumAge: Infinity },
            );
          }, 3000);
          return;
        }
        applyIpLocation();
      },
      options,
    );
  }, [applyUserPosition, applyIpLocation]);

  useEffect(() => {
    if (usesExternalUserLocation) return;

    if (!showIA || !isMapReady) {
      hasAutoRequestedLocationRef.current = false;
      isLocatingRef.current = false;
      setInternalUserLocation(null);
      return;
    }

    if (hasAutoRequestedLocationRef.current) return;
    hasAutoRequestedLocationRef.current = true;
    requestUserLocation();
  }, [
    usesExternalUserLocation,
    showIA,
    isMapReady,
    requestUserLocation,
  ]);

  useEffect(() => {
    if (usesExternalUserLocation) return;
    onUserLocationChange?.(internalUserLocation);
  }, [usesExternalUserLocation, internalUserLocation, onUserLocationChange]);

  const handleRemoveCard = useCallback((service: Service) => {
    setDismissedCardIds((prev) => new Set(prev).add(service.id));
  }, []);

  const handleRemoveAllCards = useCallback(() => {
    setDismissedCardIds((prev) => {
      const toDismiss = carouselServices
        .filter((s) => !prev.has(s.id))
        .map((s) => s.id);
      return new Set([...prev, ...toDismiss]);
    });
  }, [carouselServices]);

  const getPopupAnchor = (longitude, latitude) => {
    const map = mapRef.current?.getMap();
    if (!map) return "bottom";
    const { width, height } = map.getCanvas().getBoundingClientRect();
    const pixelPosition = map.project([longitude, latitude]);
    if (pixelPosition.x < width / 4) return "left";
    if (pixelPosition.x > (3 * width) / 4) return "right";
    if (pixelPosition.y < height / 4) return "top";
    return "bottom";
  };

  const customMarkerIcon = (category?: number) => {
    const categorySelected = categoryById.get(category);
    const useMarkerPalette =
      (isSwitchboard || isOrPrograms) &&
      isDefaultSwitchboardCategoryColor(categorySelected?.color);
    const accent = resolvePinAccent(categorySelected?.color, isSwitchboard || isOrPrograms);
    const IconComponent = categorySelected
      ? iconMapping[categorySelected.icon]
      : null;
    return (
      <span
        className={
          useMarkerPalette ? "map-pin map-pin--marker-palette" : "map-pin"
        }
        style={
          useMarkerPalette
            ? undefined
            : { ["--pin-accent" as any]: accent }
        }
      >
        {IconComponent ? <IconComponent /> : null}
      </span>
    );
  };

  const mapMarkers = useMemo(
    () => buildMapMarkers(clusters),
    [clusters],
  );

  const mapServicesKey = useMemo(
    () =>
      services
        ?.filter((x) => x?.location?.length > 1)
        .map(
          (service) =>
            `${service.id}:${service.location![0]},${service.location![1]}:${service.categories?.[0] ?? ""}`,
        )
        .join("|") ?? "",
    [services],
  );

  const cluster = useMemo(() => {
    const newCluster = new supercluster({
      radius: 40,
      maxZoom: 16,
    });

    newCluster.load(
      services
        ?.filter((x) => x?.location?.length > 1)
        .map((service) => ({
          type: "Feature",
          properties: {
            cluster: false,
            serviceId: service.id,
            category: service?.categories[0],
          },
          geometry: {
            type: "Point",
            coordinates: [+service.location[1], +service.location[0]],
          },
        })),
    );
    return newCluster;
  }, [mapServicesKey]);

  const clusterRafRef = useRef<number | null>(null);
  const debouncedUpdateClusters = useCallback(() => {
    // Coalesce rapid move/idle events into one update per animation frame so
    // clusters recompute smoothly instead of thrashing setState mid-zoom.
    if (clusterRafRef.current != null) {
      cancelAnimationFrame(clusterRafRef.current);
    }
    clusterRafRef.current = requestAnimationFrame(() => {
      clusterRafRef.current = null;
      const map = mapRef.current?.getMap();
      if (!map) return;
      const bounds = map.getBounds();
      const bbox: [number, number, number, number] = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
      const newClusters = cluster.getClusters(bbox, Math.floor(map.getZoom()));
      setClusters(newClusters);
    });
  }, [cluster]);

  const calculateBounds = (services: Service[]) => {
    const points = services
      ?.filter((x) => x?.location?.length > 1)
      .map((service) => [+service.location[1], +service.location[0]]);

    if (!points.length) return null;

    const bounds = points.reduce(
      (bounds, point) => {
        return {
          minLng: Math.min(bounds.minLng, point[0]),
          maxLng: Math.max(bounds.maxLng, point[0]),
          minLat: Math.min(bounds.minLat, point[1]),
          maxLat: Math.max(bounds.maxLat, point[1]),
        };
      },
      {
        minLng: points[0][0],
        maxLng: points[0][0],
        minLat: points[0][1],
        maxLat: points[0][1],
      },
    );

    return bounds;
  };
  useEffect(() => {
    if (!isMapReady) return;

    const map = mapRef.current?.getMap();
    if (!map) return;

    const handleMapIdle = () => {
      debouncedUpdateClusters();
    };

    map.on("idle", handleMapIdle);

    return () => {
      map.off("idle", handleMapIdle);
    };
  }, [isMapReady, debouncedUpdateClusters]);

  useEffect(() => {
    if (!isMapReady) return;
    debouncedUpdateClusters();
  }, [isMapReady, mapServicesKey, debouncedUpdateClusters]);

  useEffect(() => {
    if (!isMapReady) return;

    const bounds = calculateBounds(services);
    if (bounds) {
      const boundsObj = [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ];
      mapRef.current?.fitBounds(boundsObj, {
        padding: 20,
        duration: 0,
      });
      const currentZoom = mapRef.current?.getZoom();
      if (currentZoom) {
        mapRef.current?.setZoom(currentZoom - 1);
      }
    } else if (latitude && longitude) {
      mapRef.current?.setCenter([longitude, latitude]);
    }
  }, [isMapReady, mapServicesKey, latitude, longitude]);

  useEffect(() => {
    if (serviceToNavigate && isMapReady) {
      const service = serviceToNavigate;
      setSelectedCardServiceId(service.id);
      setPopupInfo(null);
      setPopupCoords(null);
      setIsMapMoving(true);
      const map = mapRef.current?.getMap();
      if (map && service.location && service.location.length > 1) {
        const [longitude, latitude] = [
          +service.location[1],
          +service.location[0],
        ];

        const showPopup = () => {
          setIsMapMoving(false);
          setPopupCoords({ lng: longitude, lat: latitude });
          setPopupInfo(service);
          onNavigateComplete?.();
        };

        const fallbackTimeout = setTimeout(showPopup, 1100);

        map.flyTo({
          center: [longitude, latitude],
          zoom: Math.max(map.getZoom(), 14),
          duration: 1000,
        });
        map.once("moveend", () => {
          clearTimeout(fallbackTimeout);
          showPopup();
        });
      } else {
        setIsMapMoving(false);
        setPopupInfo(service);
        onNavigateComplete?.();
      }
    }
  }, [serviceToNavigate, isMapReady, onNavigateComplete]);

  const handleCardClick = (service: Service) => {
    setSelectedCardServiceId(service.id);
    if (service.location && service.location.length > 1) {
      openServicePopup(service, +service.location[1], +service.location[0]);
    } else {
      setPopupInfo(null);
      setPopupCoords(null);
      setIsMapMoving(false);
      setPopupInfo(service);
    }
  };

  const expandMarkerGroup = useCallback(
    (
      id: string,
      lng: number,
      lat: number,
      points: Array<{ serviceId: number; category?: number }>,
    ) => {
      if (!points.length) return;
      setExpandedGroup({ id, lng, lat, points });
      setPopupInfo(null);
      setPopupCoords(null);
    },
    [],
  );

  const renderSpiderMarkers = (group: ExpandedMarkerGroup) =>
    group.points.map((point, index) => {
      const service = serviceById.get(point.serviceId);
      if (!service) return null;
      const offset = spiderPixelOffset(index, group.points.length);

      return (
        <Marker
          key={`spider-${group.id}-${point.serviceId}`}
          longitude={group.lng}
          latitude={group.lat}
          style={{ zIndex: index + 1 }}
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            const map = mapRef.current?.getMap();
            const coords = map
              ? pixelOffsetToLatLng(
                  map,
                  group.lng,
                  group.lat,
                  offset.x,
                  offset.y,
                )
              : { lng: group.lng, lat: group.lat };
            trackServiceClick({ service, source: "map_marker" });
            openServicePopup(service, coords.lng, coords.lat, true);
          }}
        >
          <div
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          >
            {customMarkerIcon(point.category)}
          </div>
        </Marker>
      );
    });

  const openServicePopup = useCallback(
    (
      service: Service,
      longitude: number,
      latitude: number,
      preserveExpanded = false,
    ) => {
      setPopupInfo(null);
      setPopupCoords({ lng: longitude, lat: latitude });

      const showPopup = () => {
        setIsMapMoving(false);
        setPopupInfo(service);
      };

      const map = mapRef.current?.getMap();

      if (preserveExpanded) {
        showPopup();
        return;
      }

      setIsMapMoving(true);
      const adjustedLatitude = moveMapByPixels(map, longitude, latitude, -80);
      const fallbackTimeout = setTimeout(showPopup, 600);

      map?.flyTo({
        center: [longitude, adjustedLatitude],
        duration: 500,
      });
      map?.once("moveend", () => {
        clearTimeout(fallbackTimeout);
        showPopup();
      });
    },
    [],
  );

  return (
    <div className="service-map relative w-full max-w-full min-w-0">
      <div className="w-full max-w-full min-w-0 h-[700px] md:h-[80vh] bg-indigo-200 map-container rounded-2xl relative overflow-hidden">
        <Suspense fallback={<div>Loading map...</div>}>
          <MapGL
            mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
            mapStyle={mapStyle}
            maxTileCacheSize={512}
            fadeDuration={0}
            minZoom={isSwitchboard || isOrPrograms ? 2.5 : 3}
            ref={mapRef}
            maxZoom={18}
            maxBounds={isSwitchboard || isOrPrograms ? SWITCHBOARD_MAX_BOUNDS : undefined}
            initialViewState={
              isSwitchboard || isOrPrograms
                ? {
                    longitude: longitude ?? SWITCHBOARD_DEFAULT_CENTER.longitude,
                    latitude: latitude ?? SWITCHBOARD_DEFAULT_CENTER.latitude,
                    zoom: zoom ?? SWITCHBOARD_DEFAULT_CENTER.zoom,
                  }
                : undefined
            }
            cooperativeGestures={false}
            crossSourceCollisions={false}
            optimizeForTerrain={true}
            onLoad={() => {
              setIsMapReady(true);
            }}
            onClick={() => {
              if (popupInfo) return;
              setExpandedGroup(null);
            }}
            onMoveEnd={() => {
              debouncedUpdateClusters();
              if (preserveExpandedOnMoveRef.current) {
                preserveExpandedOnMoveRef.current = false;
                return;
              }
              setExpandedGroup(null);
            }}
            style={{ borderRadius: "1rem", width: "100%", height: "100%" }}
            projection={{ name: "mercator" }}
          >
            <style>{`
              .ai-search-overlay { position: absolute; top: 10px; left: 10px; z-index: 10; }
            `}</style>
            {app.country !== 42 && <GeocoderControl
              ref={geocoderRef}
              mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
              position="top-left"
            />}

            <NavigationControl position="top-left" />
            <MapStyleControlComponent
              setMapStyle={setMapStyle}
              currentMapStyle={mapStyle}
            />
            {mapMarkers.flatMap((marker) => {
              if (marker.kind === "cluster") {
                const clusterItem = marker.data;
                const [longitude, latitude] = clusterItem.geometry.coordinates;
                const {
                  point_count: pointCount,
                  point_count_abbreviated: pointCountAbbreviated,
                } = clusterItem.properties;
                const expandId = coordKey(longitude, latitude);

                if (expandedGroup?.id === expandId) {
                  return renderSpiderMarkers(expandedGroup).filter(Boolean);
                }

                return [
                  <Marker
                    key={`cluster-${expandId}`}
                    longitude={longitude}
                    latitude={latitude}
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      const leaves = cluster.getLeaves(
                        clusterItem.id as number,
                        Infinity,
                      );

                      if (areColocatedLeaves(leaves)) {
                        expandMarkerGroup(
                          expandId,
                          longitude,
                          latitude,
                          leaves.map((leaf) => ({
                            serviceId: leaf.properties.serviceId as number,
                            category: leaf.properties.category as number,
                          })),
                        );
                        const map = mapRef.current?.getMap();
                        if ((map?.getZoom() || 0) < 15) {
                          preserveExpandedOnMoveRef.current = true;
                          map?.flyTo({
                            center: [longitude, latitude],
                            zoom: 15,
                            duration: 400,
                          });
                        }
                        return;
                      }

                      setExpandedGroup(null);
                      const map = mapRef.current?.getMap();
                      const currentZoom = map?.getZoom() || 0;
                      map?.flyTo({
                        center: [longitude, latitude],
                        zoom: Math.min(currentZoom + 3, 18),
                        duration: 500,
                      });
                    }}
                  >
                    <div
                      className="cluster-marker"
                      style={{
                        width: `32px`,
                        height: `32px`,
                      }}
                    >
                      {pointCountAbbreviated || pointCount}
                    </div>
                  </Marker>,
                ];
              }

              if (marker.kind === "colocated") {
                if (expandedGroup?.id === marker.key) {
                  return renderSpiderMarkers(expandedGroup).filter(Boolean);
                }

                return [
                  <Marker
                    key={`colocated-${marker.key}`}
                    longitude={marker.lng}
                    latitude={marker.lat}
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      expandMarkerGroup(
                        marker.key,
                        marker.lng,
                        marker.lat,
                        marker.points.map((point) => ({
                          serviceId: point.properties.serviceId as number,
                          category: point.properties.category as number,
                        })),
                      );
                      const map = mapRef.current?.getMap();
                      if ((map?.getZoom() || 0) < 15) {
                        preserveExpandedOnMoveRef.current = true;
                        map?.flyTo({
                          center: [marker.lng, marker.lat],
                          zoom: 15,
                          duration: 400,
                        });
                      }
                    }}
                  >
                    <div
                      className="cluster-marker"
                      style={{
                        width: `32px`,
                        height: `32px`,
                      }}
                    >
                      {marker.points.length}
                    </div>
                  </Marker>,
                ];
              }

              const clusterItem = marker.data;
              const [longitude, latitude] = clusterItem.geometry.coordinates;
              const service = serviceById.get(
                clusterItem.properties.serviceId as number,
              );
              if (!service) return null;

              return [
                <Marker
                  key={`marker-${clusterItem.properties.serviceId}`}
                  longitude={longitude}
                  latitude={latitude}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setExpandedGroup(null);
                    trackServiceClick({ service, source: "map_marker" });
                    openServicePopup(service, longitude, latitude);
                  }}
                >
                  {customMarkerIcon(clusterItem.properties.category)}
                </Marker>,
              ];
            })}
            {showIA && userLocation && (
              <Marker
                longitude={userLocation.longitude}
                latitude={userLocation.latitude}
                anchor="center"
              >
                <div
                  className="relative flex h-8 w-8 items-center justify-center"
                  aria-label={translate(translations.yourLocation)}
                >
                  <span className="absolute h-8 w-8 rounded-full bg-red-500/25 animate-ping" />
                  <span className="relative h-4 w-4 rounded-full border-2 border-white bg-red-500 shadow-md" />
                </div>
              </Marker>
            )}
            {showPopup && popupInfo && popupCoords && !isMapMoving && (
              <Popup
                longitude={popupCoords.lng}
                latitude={popupCoords.lat}
                onClose={() => {
                  setPopupInfo(null);
                  setPopupCoords(null);
                }}
                maxWidth="417px"
                anchor={getPopupAnchor(popupCoords.lng, popupCoords.lat)}
                offset={20}
              >
                <div className="popup-content">
                  <h3
                    className="popup-name"
                    dir={isSwitchboard ? "ltr" : undefined}
                    style={isSwitchboard ? { direction: "ltr", unicodeBidi: "isolate", textAlign: "left" } : undefined}
                  >
                    {displayServiceName(popupInfo.name)}
                  </h3>
                  {popupInfo.address && (
                    <div
                      className="popup-address"
                      dir={isSwitchboard ? "ltr" : undefined}
                      style={isSwitchboard ? { direction: "ltr", unicodeBidi: "isolate", textAlign: "left" } : undefined}
                    >
                      <PinDrop className="popup-address__icon" aria-hidden="true" />
                      <span>{popupInfo.address}</span>
                    </div>
                  )}
                  {translate(popupInfo.description) && (
                    <p className="popup-desc">
                      {stripHtmlTags(translate(popupInfo.description) || "")?.slice(
                        0,
                        120,
                      )}
                      &hellip;
                    </p>
                  )}
                  <div className="popup-actions">
                    {popupInfo.address && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(popupInfo.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="popup-link"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="15"
                          height="15"
                          className="popup-link__icon"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path
                            fill="currentColor"
                            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                          />
                          <circle cx="12" cy="9" r="2.5" fill="white" />
                        </svg>
                        {translate(translations.directions)}
                      </a>
                    )}
                    <a
                      href={`/${locale.toLowerCase()}/services/${popupInfo.id}`}
                      target="_blank"
                      className="popup-link popup-link--primary"
                      onClick={() =>
                        trackServiceDetailsClick({
                          service: popupInfo,
                          source: "map_popup",
                        })
                      }
                    >
                      {translate(translations.seeMoreDetails)}
                      <RightOutlined aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </Popup>
            )}
          </MapGL>
        </Suspense>

        <div
          className="absolute bottom-0 left-0 right-0 z-10 transition-transform duration-500 ease-out"
          style={{
            transform: showCompanyCards ? "translateY(0)" : "translateY(100%)",
          }}
        >
          <MapCompanyCards
            services={displayedCarouselServices}
            onCardClick={handleCardClick}
            onRemoveCard={handleRemoveCard}
            onRemoveAll={handleRemoveAllCards}
            selectedServiceId={selectedCardServiceId}
          />
        </div>
      </div>
    </div>
  );
}
